import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Award, Lock, Star, Medal, Compass, Flame, TrendingUp, Trophy, Target, GraduationCap, Brain, Share2 } from "lucide-react";
import { toast } from "sonner";

const ICON_MAP = {
  star: Star, medal: Medal, compass: Compass, flame: Flame, trending: TrendingUp,
  trophy: Trophy, target: Target, graduate: GraduationCap, brain: Brain,
};

export default function Achievements() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    load();
    // eslint-disable-next-line
  }, [user, loading]);

  const load = async () => {
    try {
      // trigger a scan for any newly qualified achievements
      await api.post("/achievements/check");
      const { data } = await api.get("/achievements/my");
      setItems(data);
    } catch (err) { console.warn("[Achievements] load failed:", err?.message); }
  };

  const share = async (a) => {
    const text = `Acabei de desbloquear "${a.title}" no CRIA FINANCE! ${a.desc} — cria.finance`;
    if (navigator.share) {
      try { await navigator.share({ title: a.title, text }); return; } catch (err) { console.warn("[Achievements] native share aborted:", err?.message); }
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiado! Cola no teu Insta Story.");
    } catch { toast.error("Não deu pra copiar"); }
  };

  if (!user) return null;

  const owned = items.filter(i => i.unlocked).length;

  return (
    <div data-testid="achievements-page" className="grid-bg min-h-[85vh]">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <span className="sticker">CONQUISTAS</span>
        <h1 className="mt-6 font-bebas text-6xl md:text-7xl tracking-tighter">
          TUAS <span className="text-[#CCFF00]">MEDALHAS</span>
        </h1>
        <p className="mt-3 font-manrope text-white/60">Você tem {owned} de {items.length} desbloqueadas</p>

        <div className="mt-10 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {items.map((a) => {
            const Icon = ICON_MAP[a.icon] || Award;
            return (
              <div
                key={a.id}
                data-testid={`ach-${a.id}`}
                className="brutal-card"
                style={{
                  borderColor: a.unlocked ? a.color : "#333",
                  opacity: a.unlocked ? 1 : 0.5,
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="w-14 h-14 border-2 flex items-center justify-center" style={{borderColor: a.unlocked ? a.color : "#555"}}>
                    {a.unlocked ? <Icon style={{color: a.color}} size={28}/> : <Lock className="text-white/40" size={24}/>}
                  </div>
                  <span className="font-bebas text-lg" style={{color: a.unlocked ? a.color : "#666"}}>+{a.xp} XP</span>
                </div>
                <h3 className="mt-4 font-bebas text-2xl tracking-tighter">{a.title}</h3>
                <p className="mt-1 text-white/60 font-manrope text-sm">{a.desc}</p>
                {a.unlocked ? (
                  <>
                    <div className="mt-3 text-white/40 font-manrope text-xs">
                      Conquistado em {new Date(a.unlocked_at).toLocaleDateString("pt-BR")}
                    </div>
                    <button
                      data-testid={`ach-share-${a.id}`}
                      onClick={()=>share(a)}
                      className="mt-3 flex items-center gap-2 text-[#CCFF00] font-manrope text-xs uppercase tracking-widest hover:underline"
                    >
                      <Share2 size={14}/> Compartilhar
                    </button>
                  </>
                ) : (
                  <div className="mt-3 text-white/30 font-manrope text-xs italic">Bloqueado</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
